export type ExtractedAccentColors = {
  primary: string;
  highlight: string;
  deep: string;
  /** Dominant color sampled from the poster border (ideal page background tint). */
  background: string;
};

const DEFAULT_EDGE_BACKGROUND = "#060B1E";

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h * 360, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  h /= 360;
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const hue2rgb = (p: number, q: number, t: number) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    Math.round(hue2rgb(p, q, h) * 255),
    Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  ];
}

function toHex(r: number, g: number, b: number): string {
  const pad = (n: number) => n.toString(16).padStart(2, "0");
  return `#${pad(r)}${pad(g)}${pad(b)}`;
}

function adjustLightness(hex: string, delta: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const [h, s, l] = rgbToHsl(r, g, b);
  const nl = Math.max(0.08, Math.min(0.92, l + delta));
  const [nr, ng, nb] = hslToRgb(h, s, nl);
  return toHex(nr, ng, nb);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load image for color extraction"));
    img.src = src;
  });
}

/** Average RGB along the poster perimeter (ignores letterbox padding). */
function extractEdgeBackgroundColor(
  data: Uint8ClampedArray,
  size: number,
  ox: number,
  oy: number,
  w: number,
  h: number,
): string {
  const x0 = Math.ceil(ox);
  const y0 = Math.ceil(oy);
  const x1 = Math.floor(ox + w) - 1;
  const y1 = Math.floor(oy + h) - 1;
  if (x1 <= x0 || y1 <= y0) return DEFAULT_EDGE_BACKGROUND;

  const edgeBand = Math.max(2, Math.min(5, Math.floor(Math.min(w, h) * 0.08)));
  let rSum = 0;
  let gSum = 0;
  let bSum = 0;
  let count = 0;

  const sample = (x: number, y: number) => {
    if (x < x0 || x > x1 || y < y0 || y > y1) return;
    const i = (y * size + x) * 4;
    const a = data[i + 3]!;
    if (a < 64) return;
    rSum += data[i]!;
    gSum += data[i + 1]!;
    bSum += data[i + 2]!;
    count += 1;
  };

  for (let band = 0; band < edgeBand; band++) {
    for (let x = x0; x <= x1; x++) {
      sample(x, y0 + band);
      sample(x, y1 - band);
    }
    for (let y = y0; y <= y1; y++) {
      sample(x0 + band, y);
      sample(x1 - band, y);
    }
  }

  if (count === 0) return DEFAULT_EDGE_BACKGROUND;
  return toHex(
    Math.round(rSum / count),
    Math.round(gSum / count),
    Math.round(bSum / count),
  );
}

/**
 * Extract accent colors and an edge-based background tint from a poster image URL.
 * Accents use center-weighted sampling; background uses poster border pixels.
 */
export async function extractAccentColors(src: string): Promise<ExtractedAccentColors> {
  const img = await loadImage(src);
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not available");

  const scale = Math.min(size / img.naturalWidth, size / img.naturalHeight);
  const w = img.naturalWidth * scale;
  const h = img.naturalHeight * scale;
  const ox = (size - w) / 2;
  const oy = (size - h) / 2;
  ctx.drawImage(img, ox, oy, w, h);

  const { data } = ctx.getImageData(0, 0, size, size);
  const buckets = new Map<number, { score: number; r: number; g: number; b: number }>();
  const cx = size / 2;
  const cy = size / 2;
  const maxDist = Math.sqrt(cx * cx + cy * cy);

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      const r = data[i]!;
      const g = data[i + 1]!;
      const b = data[i + 2]!;
      const [, s, l] = rgbToHsl(r, g, b);
      if (l < 0.08 || l > 0.92 || s < 0.18) continue;
      const dist = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2);
      const weight = 1 + (1 - dist / maxDist) * 2;
      const score = s * l * (1 - Math.abs(l - 0.45)) * weight;
      const hueBin = Math.floor(rgbToHsl(r, g, b)[0] / 12);
      const prev = buckets.get(hueBin);
      if (!prev || score > prev.score) {
        buckets.set(hueBin, { score, r, g, b });
      }
    }
  }

  let best = { score: 0, r: 242, g: 92, b: 31 };
  for (const v of buckets.values()) {
    if (v.score > best.score) best = v;
  }

  const primary = toHex(best.r, best.g, best.b);
  const background = extractEdgeBackgroundColor(data, size, ox, oy, w, h);
  return {
    primary,
    highlight: adjustLightness(primary, 0.14),
    deep: adjustLightness(primary, -0.18),
    background,
  };
}
